import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const FrameworksHero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      setScrollProgress(latest);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  // Parallax transforms
  const eyebrowOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const eyebrowY = useTransform(scrollYProgress, [0, 0.3], [0, -30]);
  
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const headlineY = useTransform(scrollYProgress, [0, 0.4], [0, -50]);
  const headlineScale = useTransform(scrollYProgress, [0, 0.4], [1, 0.95]);
  
  const descriptionOpacity = useTransform(scrollYProgress, [0, 0.35], [1, 0]);
  const descriptionY = useTransform(scrollYProgress, [0, 0.35], [0, -40]);

  const glowIntensity = useTransform(scrollYProgress, [0, 0.5], [0.05, 0.15]);

  return (
    <section 
      ref={containerRef}
      className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
    >
      {/* Dynamic Background Glow */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: glowIntensity }}
      >
        <div className="absolute top-20 left-10 w-96 h-96 bg-accent-blue rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-blue rounded-full blur-3xl"></div>
      </motion.div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-8 text-center">
        {/* Eyebrow */}
        <motion.p
          style={{ 
            opacity: eyebrowOpacity,
            y: eyebrowY 
          }}
          className="text-sm md:text-base text-accent-blue font-semibold mb-4 md:mb-6 uppercase tracking-wider"
        >
          Frameworks
        </motion.p>

        {/* Headline */}
        <motion.h1
          style={{ 
            opacity: headlineOpacity,
            y: headlineY,
            scale: headlineScale
          }}
          className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 md:mb-8 leading-tight"
        >
          Systems for removing
          <br className="hidden md:block" />
          <span className="text-accent-blue"> growth bottlenecks</span>
        </motion.h1>

        {/* Description */}
        <motion.p
          style={{ 
            opacity: descriptionOpacity,
            y: descriptionY 
          }}
          className="text-base md:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
        >
          Proven frameworks for identifying and removing the structural constraints that limit company growth
        </motion.p>
      </div>
    </section>
  );
};

export default FrameworksHero;